// PostgreSQL renderer-side schema introspection.
//
// S7 items 2 + 8 — every catalog query here is PAGED: a `LIMIT n OFFSET m`
// window keyed by the catalog's own `ORDER BY`, `INTROSPECTION_PAGE_ROWS` rows
// at a time, issued through the shared per-connection introspection lane
// (`introspectionSemaphore.ts`, max 2 in flight) and sunk page by page so
// `pgSchemaCache` can merge + republish incrementally. The old shape — one
// uncapped-in-practice query per artefact, all of them in a single
// `Promise.all`, each result set shipped inline over IPC with a 50,000-row cap
// — froze the renderer and silently clipped the tail of large catalogs.
//
// Paging keys, one per artefact:
//   relations   `(n.nspname, c.relname, a.attnum)`  — matviews are multi-row,
//                                                     so the page carries a
//                                                     straddling relation over
//   columns     `(table_schema, table_name, ordinal_position)`
//   foreign keys`(con.oid, k.ord)`                  — group key `con.oid`
//   routines    `(p.proname, p.oid)`                — group key `p.oid`
//   enum labels `(t.typname, e.enumsortorder)`      — group key `t.typname`
//   user types  `(n.nspname, t.typname)`
//
// The O(1) probes (`version()`, `search_path`, the extension list, the PG19
// property-graph catalogs) stay single queries — they still go through the
// lane, but there is nothing to page.

import type { RoutineMetadata, RoutineParameter } from '@/types/electron';
import type { FkEdge, PropertyGraphMeta, SequenceMeta } from '@/core/engines/intellisenseTypes';
import type {
  PgSchemaColumn,
  PgSchemaSnapshot,
  PgSchemaTableEntry,
  PgUserType,
} from './pgSchemaTypes';
import type { IntrospectionRow } from './introspectionPaging';
import { pgTableKey } from './pgRelationKey';
import {
  COLUMN_ROW_BUDGET,
  FK_ROW_BUDGET,
  ROUTINE_ROW_BUDGET,
  TABLE_ROW_BUDGET,
  TYPE_ROW_BUDGET,
} from './introspectionBudgets';
import {
  fetchIntrospectionProbeRows,
  fetchPagedArtefact,
  type PagedFetchOptions,
} from './introspectionQuery';

export type { PagedFetchOptions };

/** One page's worth of aggregated items, handed to phase 2's merge. */
export type PgPageSink<TItem> = (items: readonly TItem[]) => void | Promise<void>;

/** Run one small fixed-size probe through the introspection lane. */
async function probeRows(
  connectionId: string,
  database: string,
  label: string,
  sql: string,
): Promise<readonly IntrospectionRow[]> {
  return fetchIntrospectionProbeRows({ engine: 'pgsql', connectionId, database, label, sql });
}

/** Page one pgsql artefact through the shared loop. */
async function pagePgArtefact<TItem>(args: {
  connectionId: string;
  database: string;
  label: string;
  artefact: Parameters<typeof fetchPagedArtefact>[0]['artefact'];
  totalBudget: number;
  sqlForPage: (pageClause: string) => string;
  aggregate: (rows: readonly IntrospectionRow[]) => TItem[];
  sink: PgPageSink<TItem>;
  groupKeyOf?: (row: IntrospectionRow) => string;
  shouldAbort?: () => boolean;
}): Promise<void> {
  await fetchPagedArtefact<TItem>({ engine: 'pgsql', dialect: 'limit-offset', ...args });
}

/**
 * Parse the output of `pg_get_function_arguments(oid)` into RoutineParameter[].
 * Mirrors the parser in electron/metadataService.ts (renderer can't import electron code).
 */
export function parsePgArgs(argsFormatted: string): RoutineParameter[] {
  if (!argsFormatted?.trim()) return [];

  const params: RoutineParameter[] = [];
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < argsFormatted.length; i++) {
    if (argsFormatted[i] === '(') depth++;
    else if (argsFormatted[i] === ')') depth--;
    else if (argsFormatted[i] === ',' && depth === 0) {
      parts.push(argsFormatted.slice(start, i).trim());
      start = i + 1;
    }
  }
  parts.push(argsFormatted.slice(start).trim());

  for (const part of parts) {
    if (!part) continue;
    const tokens = part.split(/\s+/);
    let idx = 0;
    let isOutput = false;
    const dirUpper = tokens[0]?.toUpperCase();
    if (dirUpper === 'OUT' || dirUpper === 'INOUT') { isOutput = true; idx++; }
    else if (dirUpper === 'IN' || dirUpper === 'VARIADIC') { idx++; }

    const remaining = tokens.slice(idx);
    const defaultIdx = remaining.findIndex(t => t.toUpperCase() === 'DEFAULT');
    const typeParts = defaultIdx >= 0 ? remaining.slice(0, defaultIdx) : remaining;

    let name = '';
    let dataType = '';
    if (typeParts.length >= 2) { name = typeParts[0]; dataType = typeParts.slice(1).join(' '); }
    else if (typeParts.length === 1) { dataType = typeParts[0]; }

    params.push({ name, dataType, isOutput });
  }
  return params;
}

/** Pure aggregator (mirrors mysql's `aggregateMysqlRoutineRows`) — exposed
 *  for unit tests. `prokind 'p'` → routineType 'PROCEDURE' so
 *  `projectRoutines` stops mislabeling pg procedures as functions;
 *  `return_type` (pg_get_function_result — `integer`, `double precision`,
 *  `void`, `SETOF record`, …) is carried verbatim for the value-slot
 *  return-type policy (spec §A1). */
export function aggregatePgRoutineRows(
  rows: ReadonlyArray<Record<string, unknown>>,
): Record<string, RoutineMetadata[]> {
  const routinesByNameLower: Record<string, RoutineMetadata[]> = {};
  for (const row of rows) {
    const name = String(row.routine_name ?? '');
    if (!name) continue;
    const returnType = row.return_type != null ? String(row.return_type) : undefined;
    const metadata: RoutineMetadata = {
      schema: String(row.routine_schema ?? 'public'),
      name,
      ...(String(row.prokind ?? '') === 'p' ? { routineType: 'PROCEDURE' } : {}),
      ...(returnType ? { returns: returnType } : {}),
      parameters: parsePgArgs(String(row.args_formatted ?? '')),
    };
    const key = name.toLowerCase();
    (routinesByNameLower[key] ??= []).push(metadata);
  }
  return routinesByNameLower;
}

/** One routine, flat — the cache buckets these by lower-cased name. */
export function mapPgRoutineRows(
  rows: readonly IntrospectionRow[],
): RoutineMetadata[] {
  const out: RoutineMetadata[] = [];
  for (const row of rows) {
    const name = String(row.routine_name ?? '');
    if (!name) continue;
    const returnType = row.return_type != null ? String(row.return_type) : undefined;
    out.push({
      schema: String(row.routine_schema ?? 'public'),
      name,
      ...(String(row.prokind ?? '') === 'p' ? { routineType: 'PROCEDURE' } : {}),
      ...(returnType ? { returns: returnType } : {}),
      parameters: parsePgArgs(String(row.args_formatted ?? '')),
    });
  }
  return out;
}

export function buildPgRoutinesPageSql(pageClause: string): string {
  return `SELECT
      n.nspname AS routine_schema,
      p.proname AS routine_name,
      pg_get_function_arguments(p.oid) AS args_formatted,
      p.prokind AS prokind,
      pg_get_function_result(p.oid) AS return_type
    FROM pg_catalog.pg_proc p
    JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
    WHERE n.nspname NOT IN ('pg_catalog', 'information_schema')
      AND p.prokind IN ('f', 'p')
    ORDER BY p.proname, p.oid
    ${pageClause}`;
}

/** Page every user routine into `sink`, one page at a time (phase 2). */
export async function fetchPgRoutinePages(
  connectionId: string,
  database: string,
  sink: PgPageSink<RoutineMetadata>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pagePgArtefact<RoutineMetadata>({
    connectionId,
    database,
    label: 'routines',
    artefact: 'routines',
    totalBudget: ROUTINE_ROW_BUDGET,
    sqlForPage: buildPgRoutinesPageSql,
    aggregate: mapPgRoutineRows,
    sink,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper — the pre-tiering shape, kept for `buildPgSchemaSnapshot`. */
async function fetchRoutines(
  connectionId: string,
  database: string,
): Promise<Record<string, RoutineMetadata[]>> {
  const byName: Record<string, RoutineMetadata[]> = {};
  await fetchPgRoutinePages(connectionId, database, (page) => {
    for (const routine of page) {
      (byName[routine.name.toLowerCase()] ??= []).push(routine);
    }
  });
  return byName;
}

/**
 * Parse `current_setting('search_path')` into schema names, the way
 * PostgreSQL itself reads it (`SplitIdentifierString`): comma-separated
 * entries, whitespace around them ignored; a `"…"` entry is taken verbatim
 * (`""` is one `"`, a comma inside is part of the name), an unquoted one is
 * down-cased. The entry `$user` (unquoted or quoted) names the schema of the
 * current role (`currentUser`), and is dropped when that is unknown.
 *
 * The names come back lower-cased and deduped — the resolvers compare schema
 * names folded. `''` is an EMPTY path (a successful answer: no bare name
 * resolves); `null` means the value could not be read (malformed), which
 * callers treat like a failed probe.
 */
export function parseSearchPath(showResult: string, currentUser: string | null): string[] | null {
  const entries = splitPgIdentifierList(showResult);
  if (!entries) return null;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const resolved = entry === '$user' ? currentUser : entry;
    if (!resolved) continue;
    const lower = resolved.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(lower);
  }
  return out;
}

/** PostgreSQL's `SplitIdentifierString(…, ',')`: the entries, or `null` on a syntax error. */
function splitPgIdentifierList(text: string): string[] | null {
  const out: string[] = [];
  // `scanner_isspace`: exactly these six, no Unicode spaces.
  const isSpace = (ch: string | undefined): boolean => ch !== undefined && ' \t\n\r\f\v'.includes(ch);
  let i = 0;
  while (isSpace(text[i])) i++;
  if (i === text.length) return out;
  for (;;) {
    let name: string;
    if (text[i] === '"') {
      let j = i + 1;
      name = '';
      for (;;) {
        const close = text.indexOf('"', j);
        if (close < 0) return null; // unterminated quoted name
        name += text.slice(j, close);
        if (text[close + 1] !== '"') {
          i = close + 1;
          break;
        }
        name += '"';
        j = close + 2;
      }
    } else {
      const start = i;
      while (i < text.length && text[i] !== ',' && !isSpace(text[i])) i++;
      if (i === start) return null; // empty unquoted name
      name = text.slice(start, i).toLowerCase();
    }
    out.push(name);
    while (isSpace(text[i])) i++;
    if (i === text.length) return out;
    if (text[i] !== ',') return null;
    i++;
    while (isSpace(text[i])) i++;
  }
}

/**
 * Aggregate per-column rows from `pg_constraint` × `pg_attribute` into one
 * `FkEdge` per constraint. Exported so the unit tests can exercise the
 * grouping/sorting without standing up the IPC bridge — same pattern
 * `mssqlSchemaIntrospection.aggregateFkRows` uses.
 */
export function aggregatePgFkRows(rows: readonly Record<string, unknown>[]): FkEdge[] {
  type Pending = {
    fkOid: string;
    name: string;
    fromSchema: string;
    fromTable: string;
    toSchema: string;
    toTable: string;
    fromCols: { ord: number; col: string }[];
    toCols: { ord: number; col: string }[];
  };

  const byFkOid = new Map<string, Pending>();
  for (const raw of rows) {
    const fkOid = String(raw.fk_oid ?? '').trim();
    const fromTable = String(raw.from_table ?? '').trim();
    const toTable = String(raw.to_table ?? '').trim();
    const fromColumn = String(raw.from_column ?? '').trim();
    const toColumn = String(raw.to_column ?? '').trim();
    if (!fkOid || !fromTable || !toTable || !fromColumn || !toColumn) continue;
    const ord = Number(raw.ord ?? 0);
    let entry = byFkOid.get(fkOid);
    if (!entry) {
      entry = {
        fkOid,
        name: String(raw.fk_name ?? '').trim(),
        fromSchema: String(raw.from_schema ?? '').trim(),
        fromTable,
        toSchema: String(raw.to_schema ?? '').trim(),
        toTable,
        fromCols: [],
        toCols: [],
      };
      byFkOid.set(fkOid, entry);
    }
    entry.fromCols.push({ ord, col: fromColumn });
    entry.toCols.push({ ord, col: toColumn });
  }

  const out: FkEdge[] = [];
  for (const entry of byFkOid.values()) {
    entry.fromCols.sort((a, b) => a.ord - b.ord);
    entry.toCols.sort((a, b) => a.ord - b.ord);
    out.push({
      name: entry.name || undefined,
      fromSchema: entry.fromSchema || undefined,
      fromTable: entry.fromTable,
      fromColumns: entry.fromCols.map((c) => c.col),
      toSchema: entry.toSchema || undefined,
      toTable: entry.toTable,
      toColumns: entry.toCols.map((c) => c.col),
    });
  }
  return out;
}

/**
 * Fetch the database's foreign-key graph from `pg_catalog`. Composite-key
 * FKs come back as one row per `(constraint, ordinal)` pair via
 * `unnest(conkey) WITH ORDINALITY`; `aggregatePgFkRows` then groups by
 * `fk_oid` and sorts each side's column list by ordinal so composite keys
 * stay in declaration order.
 *
 * Excludes constraints whose source-table schema is `pg_catalog` /
 * `information_schema` so the resolver doesn't surface system-internal FKs
 * in user-authored queries.
 */
export function buildPgForeignKeysPageSql(pageClause: string): string {
  return `SELECT
      con.oid AS fk_oid,
      con.conname AS fk_name,
      ns_from.nspname AS from_schema,
      cls_from.relname AS from_table,
      att_from.attname AS from_column,
      ns_to.nspname AS to_schema,
      cls_to.relname AS to_table,
      att_to.attname AS to_column,
      k.ord AS ord
    FROM pg_catalog.pg_constraint con
    JOIN pg_catalog.pg_class cls_from ON cls_from.oid = con.conrelid
    JOIN pg_catalog.pg_namespace ns_from ON ns_from.oid = cls_from.relnamespace
    JOIN pg_catalog.pg_class cls_to ON cls_to.oid = con.confrelid
    JOIN pg_catalog.pg_namespace ns_to ON ns_to.oid = cls_to.relnamespace
    JOIN LATERAL unnest(con.conkey)  WITH ORDINALITY AS k(attnum, ord) ON TRUE
    JOIN LATERAL unnest(con.confkey) WITH ORDINALITY AS f(attnum, ord) ON k.ord = f.ord
    JOIN pg_catalog.pg_attribute att_from
      ON att_from.attrelid = con.conrelid AND att_from.attnum = k.attnum
    JOIN pg_catalog.pg_attribute att_to
      ON att_to.attrelid = con.confrelid AND att_to.attnum = f.attnum
    WHERE con.contype = 'f'
      AND ns_from.nspname NOT IN ('pg_catalog', 'information_schema')
    ORDER BY con.oid, k.ord
    ${pageClause}`;
}

/**
 * Page the FK graph into `sink`. A composite FK is many rows sharing
 * `fk_oid`, so the page loop carries a straddling constraint to the next page
 * (`groupKeyOf`) — otherwise one FK split across a boundary would aggregate
 * into two half-edges with mismatched column lists.
 */
export async function fetchPgForeignKeyPages(
  connectionId: string,
  database: string,
  sink: PgPageSink<FkEdge>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pagePgArtefact<FkEdge>({
    connectionId,
    database,
    label: 'fks',
    artefact: 'foreignKeys',
    totalBudget: FK_ROW_BUDGET,
    sqlForPage: buildPgForeignKeysPageSql,
    aggregate: aggregatePgFkRows,
    sink,
    groupKeyOf: (row) => String(row.fk_oid ?? ''),
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchPgForeignKeyPages}. */
export async function fetchPgForeignKeys(
  connectionId: string,
  database: string,
): Promise<FkEdge[]> {
  const out: FkEdge[] = [];
  await fetchPgForeignKeyPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * Fetch the list of installed extensions from `pg_extension`. Names are
 * lower-cased. Empty array when the query fails. Drives pgvector operator
 * gating in the resolver.
 */
export async function fetchPgExtensions(
  connectionId: string,
  database: string,
): Promise<string[]> {
  const rows = await probeRows(
    connectionId,
    database,
    'extensions',
    'SELECT extname FROM pg_extension ORDER BY extname;',
  );
  const out: string[] = [];
  for (const row of rows) {
    const name = String(row.extname ?? '').trim().toLowerCase();
    if (name) out.push(name);
  }
  return out;
}

/**
 * Fetch user-defined enum types and their ordered member labels from
 * `pg_enum`, keyed by the bare type name (`pg_type.typname`). The column
 * introspection above stores each column's `udt_name` as `dataType`, so the
 * caller matches a column to its members by that bare name. Returns an empty
 * map when the probe fails (e.g. the user lacks `pg_catalog` read) — native
 * enum completion is then simply absent, no error surfaced.
 */
export async function fetchPgEnumTypes(
  connectionId: string,
  database: string,
  options?: PagedFetchOptions,
): Promise<Map<string, string[]>> {
  const out = new Map<string, string[]>();
  await pagePgArtefact<{ typeName: string; label: string }>({
    connectionId,
    database,
    label: 'enums',
    artefact: 'types',
    totalBudget: TYPE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT t.typname AS type_name, e.enumlabel AS label
    FROM pg_type t
    JOIN pg_enum e ON e.enumtypid = t.oid
    ORDER BY t.typname, e.enumsortorder
    ${pageClause}`,
    aggregate: (rows) => {
      const items: { typeName: string; label: string }[] = [];
      for (const row of rows) {
        const typeName = String(row.type_name ?? '').trim();
        if (!typeName) continue;
        items.push({ typeName, label: String(row.label ?? '') });
      }
      return items;
    },
    sink: (items) => {
      for (const item of items) {
        const list = out.get(item.typeName);
        if (list) list.push(item.label);
        else out.set(item.typeName, [item.label]);
      }
    },
    // A type's labels must not be split across two aggregate calls in a way
    // that reorders them; keeping the trailing type whole also keeps the
    // members in `enumsortorder`.
    groupKeyOf: (row) => String(row.type_name ?? ''),
    shouldAbort: options?.shouldAbort,
  });
  return out;
}

/**
 * `pg_class.relkind` → the relation kind the bus snapshot speaks. Ordinary /
 * partitioned / foreign tables are all "a table you can select from and (for
 * the first two) TRUNCATE"; a matview keeps its own kind because it is the
 * ONLY legal target of `REFRESH MATERIALIZED VIEW`.
 */
function relationKindOf(relkind: string): PgSchemaTableEntry['kind'] | null {
  switch (relkind) {
    case 'r':
    case 'p':
    case 'f':
      return 'table';
    case 'v':
      return 'view';
    case 'm':
      return 'materialized-view';
    default:
      return null;
  }
}

export type PgRelationCatalog = {
  /** `<schema-lower>|<name-lower>` → relation kind, for every non-system
   *  relation (tables, views, matviews, foreign/partitioned tables). */
  readonly kinds: ReadonlyMap<string, NonNullable<PgSchemaTableEntry['kind']>>;
  /**
   * Same keys → the relation's CATALOG spelling (`pg_namespace.nspname` /
   * `pg_class.relname` bytes). The key is folded for lookups; the entry built
   * from it must carry the real name — a mixed-case `"MidVqeMixed"` written
   * back as `midvqemixed` names a relation that does not exist. Optional so
   * hand-built catalogs keep working (the key then stands in).
   */
  readonly names?: ReadonlyMap<string, { readonly schema: string; readonly name: string }>;
  /** Materialized-view entries WITH their columns —
   *  `information_schema.columns` omits matviews entirely, so a matview's
   *  columns can only come from `pg_attribute`. */
  readonly matviews: readonly PgSchemaTableEntry[];
  /** `relkind = 'S'` rows, schema-qualified. */
  readonly sequences: readonly SequenceMeta[];
};

/**
 * Assemble the flat `pg_class` × `pg_attribute` scan into relation kinds,
 * matview column lists, and the sequence catalog. Exported pure so the unit
 * tests can exercise the mapping without the IPC bridge (mirrors
 * `aggregatePgFkRows`).
 *
 * Rows arrive one-per-relation for everything EXCEPT matviews, which arrive
 * one-per-column (the query left-joins `pg_attribute` only for `relkind='m'`)
 * — so a matview with N columns contributes N rows, all carrying the same
 * `table_schema` / `table_name`.
 */
export function assemblePgRelationCatalog(
  rows: readonly Record<string, unknown>[],
): PgRelationCatalog {
  const kinds = new Map<string, NonNullable<PgSchemaTableEntry['kind']>>();
  const names = new Map<string, { schema: string; name: string }>();
  const matviewsByKey = new Map<string, PgSchemaTableEntry>();
  const sequences: SequenceMeta[] = [];
  const seenSequence = new Set<string>();

  for (const row of rows) {
    const schema = String(row.table_schema ?? '').trim();
    // The catalog spelling, verbatim — and so is the key: `orders` and
    // `"Orders"` are two relations.
    const name = String(row.table_name ?? '').trim();
    const relkind = String(row.rel_kind ?? '').trim();
    if (!schema || !name || !relkind) continue;
    const key = pgRelationKey(schema, name);

    if (relkind === 'S') {
      if (seenSequence.has(key)) continue;
      seenSequence.add(key);
      sequences.push({ name, schema });
      continue;
    }

    const kind = relationKindOf(relkind);
    if (!kind) continue;
    kinds.set(key, kind);
    names.set(key, { schema, name });
    if (kind !== 'materialized-view') continue;

    let entry = matviewsByKey.get(key);
    if (!entry) {
      entry = { schema, name, columns: [], kind };
      matviewsByKey.set(key, entry);
    }
    const columnName = String(row.column_name ?? '').trim();
    if (!columnName) continue;
    const formattedType = row.formatted_type == null ? '' : String(row.formatted_type);
    entry.columns.push({
      name: columnName,
      dataType: String(row.udt_name ?? ''),
      ...(formattedType ? { formattedType } : {}),
      // A matview has no constraints of its own, so no column of it is a PK.
      isPrimaryKey: false,
    });
  }

  return { kinds, names, matviews: Array.from(matviewsByKey.values()), sequences };
}

/** Case-exact, injective `schema|name` key of a relation (`pgTableKey`). */
const pgRelationKey = pgTableKey;

/**
 * One `pg_catalog` round-trip that answers three questions the
 * `information_schema.columns` scan cannot:
 *
 *   1. **What KIND is each relation?** `information_schema` merges tables and
 *      views into one `columns` view with no relkind, so every relation
 *      reached the snapshot as `kind: 'table'` and the relation-only DDL
 *      drills (`ALTER TABLE public.▮`, `DROP TABLE public.▮`) offered views.
 *   2. **What are a MATERIALIZED VIEW's columns?** `information_schema.columns`
 *      omits matviews outright (they are not in the SQL standard), so
 *      `acact_mv_film_revenue` was absent from the snapshot entirely.
 *   3. **Which sequences exist?** `relkind = 'S'`, for the `nextval('▮')` /
 *      `currval('▮')` / `setval('▮', …)` literal slot.
 *
 * The `pg_attribute` join is gated on `c.relkind = 'm'` inside the LEFT JOIN
 * condition, so tables/views cost exactly one row each — the query stays a
 * catalog-sized scan and runs in the snapshot's existing `Promise.all`, adding
 * no round-trip to the completion hot path.
 *
 * Degrades to an empty catalog when the query fails (the caller then keeps
 * today's kind-less behaviour rather than surfacing an error).
 */
export function buildPgRelationCatalogPageSql(pageClause: string): string {
  return `SELECT
      n.nspname AS table_schema,
      c.relname AS table_name,
      c.relkind::text AS rel_kind,
      a.attname AS column_name,
      t.typname AS udt_name,
      pg_catalog.format_type(a.atttypid, a.atttypmod) AS formatted_type,
      a.attnum AS ordinal_position
    FROM pg_catalog.pg_class c
    JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
    LEFT JOIN pg_catalog.pg_attribute a
      ON c.relkind = 'm' AND a.attrelid = c.oid AND a.attnum > 0 AND NOT a.attisdropped
    LEFT JOIN pg_catalog.pg_type t ON t.oid = a.atttypid
    WHERE c.relkind IN ('r', 'p', 'v', 'm', 'f', 'S')
      AND n.nspname NOT IN ('pg_catalog', 'information_schema')
      AND n.nspname NOT LIKE 'pg_temp%'
      AND n.nspname NOT LIKE 'pg_toast%'
    ORDER BY n.nspname, c.relname, a.attnum
    ${pageClause}`;
}

/**
 * Page the relation catalog into `sink`, one `PgRelationCatalog` slice per
 * page. This is PHASE 1 of the tiered load: the relation list (plus matview
 * columns and the sequence inventory) is what `ensurePgSchema` resolves on.
 *
 * A matview contributes one row per column, so a matview straddling a page
 * boundary is carried over (`groupKeyOf` = schema+relname) rather than
 * assembled twice with half its columns each time.
 */
export async function fetchPgRelationCatalogPages(
  connectionId: string,
  database: string,
  sink: PgPageSink<PgRelationCatalog>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pagePgArtefact<PgRelationCatalog>({
    connectionId,
    database,
    label: 'relations',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: buildPgRelationCatalogPageSql,
    aggregate: (rows) => [assemblePgRelationCatalog(rows)],
    sink,
    groupKeyOf: (row) => `${String(row.table_schema ?? '')}|${String(row.table_name ?? '')}`,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchPgRelationCatalogPages}. */
export async function fetchPgRelationCatalog(
  connectionId: string,
  database: string,
): Promise<PgRelationCatalog> {
  const kinds = new Map<string, NonNullable<PgSchemaTableEntry['kind']>>();
  const names = new Map<string, { schema: string; name: string }>();
  const matviews: PgSchemaTableEntry[] = [];
  const sequences: SequenceMeta[] = [];
  await fetchPgRelationCatalogPages(connectionId, database, (pages) => {
    for (const page of pages) {
      for (const [key, kind] of page.kinds) kinds.set(key, kind);
      for (const [key, spelled] of page.names ?? []) names.set(key, spelled);
      matviews.push(...page.matviews);
      sequences.push(...page.sequences);
    }
  });
  return { kinds, names, matviews, sequences };
}

/** `pg_type.typtype` → the user-type kind the bus snapshot speaks. */
function userTypeKindOf(typtype: string): PgUserType['kind'] | null {
  switch (typtype) {
    case 'e':
      return 'enum';
    case 'd':
      return 'domain';
    case 'r':
      return 'range';
    case 'c':
      return 'composite';
    default:
      return null;
  }
}

/** Pure mapper for {@link fetchPgUserTypes} — exported for the unit tests. */
export function mapPgUserTypeRows(
  rows: readonly Record<string, unknown>[],
): PgUserType[] {
  const out: PgUserType[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const name = String(row.type_name ?? '').trim();
    const schema = String(row.type_schema ?? '').trim();
    const kind = userTypeKindOf(String(row.type_kind ?? '').trim());
    if (!name || !schema || !kind) continue;
    const key = `${schema.toLowerCase()}|${name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name, schema, kind });
  }
  return out;
}

/**
 * Fetch every user-defined type NAME that is legal at a data-type slot:
 * enums (`e`), domains (`d`), range types (`r`) and standalone composites
 * (`c`). The enum-only `pg_enum` probe above answers a different question —
 * the MEMBERS of a column's enum — and could never see a domain or a range
 * type, which is why `CREATE DOMAIN public.acact_email` and `CREATE TYPE
 * public.acact_intrange AS RANGE` were invisible at `price public.▮`.
 *
 * Two exclusions matter:
 *   - every table / view / sequence carries an implicit ROWTYPE with
 *     `typtype = 'c'`, so composites are filtered down to those whose
 *     `typrelid` relation is itself `relkind = 'c'` (a real `CREATE TYPE … AS
 *     (…)`); without this the type list would duplicate the whole table list.
 *   - array types are `typtype = 'b'` (base) and never match the IN-list, so
 *     `_mpaa_rating` needs no explicit filter.
 *
 * Returns `[]` when the probe fails — the data-type slot then falls back to
 * the built-in vocabulary alone.
 */
export function buildPgUserTypesPageSql(pageClause: string): string {
  return `SELECT n.nspname AS type_schema, t.typname AS type_name, t.typtype::text AS type_kind
    FROM pg_catalog.pg_type t
    JOIN pg_catalog.pg_namespace n ON n.oid = t.typnamespace
    WHERE t.typtype IN ('e', 'd', 'r', 'c')
      AND n.nspname NOT IN ('pg_catalog', 'information_schema')
      AND n.nspname NOT LIKE 'pg_temp%'
      AND n.nspname NOT LIKE 'pg_toast%'
      AND (
        t.typtype <> 'c'
        OR EXISTS (
          SELECT 1 FROM pg_catalog.pg_class rc
          WHERE rc.oid = t.typrelid AND rc.relkind = 'c'
        )
      )
    ORDER BY n.nspname, t.typname
    ${pageClause}`;
}

/** Page the user-defined type list into `sink` (phase 2). */
export async function fetchPgUserTypePages(
  connectionId: string,
  database: string,
  sink: PgPageSink<PgUserType>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pagePgArtefact<PgUserType>({
    connectionId,
    database,
    label: 'types',
    artefact: 'types',
    totalBudget: TYPE_ROW_BUDGET,
    sqlForPage: buildPgUserTypesPageSql,
    aggregate: mapPgUserTypeRows,
    sink,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchPgUserTypePages}. */
export async function fetchPgUserTypes(
  connectionId: string,
  database: string,
): Promise<PgUserType[]> {
  const out: PgUserType[] = [];
  await fetchPgUserTypePages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * Extract the PostgreSQL *major* version from a `version()` string. Robust
 * against the beta/rc suffixes and OS/compiler tails PG appends:
 *   `"PostgreSQL 19beta1 on aarch64-…"`            → 19
 *   `"PostgreSQL 16.4 (Debian 16.4-1.pgdg110+2) …"` → 16
 * Returns `undefined` when no leading `PostgreSQL <n>` token is present; the
 * caller then conservatively skips version-gated catalog queries.
 */
export function parsePgMajorVersion(versionString: string): number | undefined {
  const m = /\bPostgreSQL\s+(\d+)/i.exec(versionString ?? '');
  if (!m) return undefined;
  const major = Number(m[1]);
  return Number.isNaN(major) ? undefined : major;
}

/**
 * Assemble the three flat catalog result sets — graphs (`relkind='g'` scan),
 * labels-with-kind, and label-properties — into `PropertyGraphMeta[]`.
 * Exported so the unit tests can exercise the app-side join without the IPC
 * bridge (mirrors `aggregatePgFkRows`).
 *
 * Labels are grouped per graph in first-seen order; a label reused across
 * elements keeps its first-seen kind. Properties are grouped per
 * `(graph, label)` in row order and de-duped by name (a label attached to
 * multiple elements can surface the same property twice).
 */
export function assemblePgPropertyGraphs(
  graphRows: readonly Record<string, unknown>[],
  labelRows: readonly Record<string, unknown>[],
  propRows: readonly Record<string, unknown>[],
): PropertyGraphMeta[] {
  const graphOrder: string[] = [];
  const graphMeta = new Map<string, { name: string; schema: string }>();
  for (const g of graphRows) {
    const oid = String(g.graph_oid ?? '').trim();
    const name = String(g.graph_name ?? '').trim();
    if (!oid || !name) continue;
    if (!graphMeta.has(oid)) {
      graphMeta.set(oid, { name, schema: String(g.graph_schema ?? '').trim() });
      graphOrder.push(oid);
    }
  }

  const labelKind = new Map<string, 'vertex' | 'edge'>();
  const labelsByGraph = new Map<string, string[]>();
  for (const l of labelRows) {
    const oid = String(l.graph_oid ?? '').trim();
    const label = String(l.label_name ?? '').trim();
    if (!oid || !label) continue;
    const key = `${oid}|${label}`;
    if (labelKind.has(key)) continue; // first-seen kind wins
    // pg_propgraph_element.pgekind is 'v' (vertex) or 'e' (edge).
    labelKind.set(key, String(l.element_kind ?? '').toLowerCase() === 'e' ? 'edge' : 'vertex');
    const list = labelsByGraph.get(oid) ?? [];
    list.push(label);
    labelsByGraph.set(oid, list);
  }

  const propsByLabel = new Map<string, { name: string; dataType?: string }[]>();
  const seenProp = new Set<string>();
  for (const p of propRows) {
    const oid = String(p.graph_oid ?? '').trim();
    const label = String(p.label_name ?? '').trim();
    const propName = String(p.property_name ?? '').trim();
    if (!oid || !label || !propName) continue;
    const dedupeKey = `${oid}|${label}|${propName}`;
    if (seenProp.has(dedupeKey)) continue;
    seenProp.add(dedupeKey);
    const key = `${oid}|${label}`;
    const list = propsByLabel.get(key) ?? [];
    const dataType = String(p.data_type ?? '').trim();
    list.push({ name: propName, ...(dataType ? { dataType } : {}) });
    propsByLabel.set(key, list);
  }

  return graphOrder.map((oid) => {
    const meta = graphMeta.get(oid)!;
    const labelNames = labelsByGraph.get(oid) ?? [];
    return {
      name: meta.name,
      ...(meta.schema ? { owner: meta.schema } : {}),
      labels: labelNames.map((ln) => ({
        name: ln,
        kind: labelKind.get(`${oid}|${ln}`) ?? 'vertex',
        properties: propsByLabel.get(`${oid}|${ln}`) ?? [],
      })),
    };
  });
}

/**
 * Fetch SQL/PGQ property graphs from the PG19+ `pg_propgraph_*` catalogs and
 * assemble each graph's labels (vertex/edge) + per-label properties into the
 * engine-neutral `PropertyGraphMeta[]` (same contract Oracle populates in
 * Task 16). Drives graph-aware completion inside `GRAPH_TABLE(...)`.
 *
 * PROBE-PINNED against live PG19 (19beta1, db `dvdrental`, graph `film_graph`)
 * on 2026-07-11. A graph is a `pg_class` row with `relkind='g'`; its shape
 * lives in five catalogs. Observed column lists (`\d pg_catalog.pg_propgraph_*`):
 *   pg_propgraph_label          (oid, pglpgid → graph oid, pgllabel → label name)
 *   pg_propgraph_element        (oid, pgepgid, pgerelid, pgealias, pgekind ['v'|'e'], …)
 *   pg_propgraph_element_label  (oid, pgellabelid → label oid, pgelelid → element oid)
 *   pg_propgraph_property       (oid, pgppgid, pgpname → prop name, pgptypid, pgptypmod, …)
 *   pg_propgraph_label_property (oid, plppropid → property oid, plpellabelid → element-label oid, plpexpr)
 * A label's kind comes from the element carrying it (`pgekind`); properties
 * attach to an *element-label* (`plpellabelid` → `pg_propgraph_element_label.oid`),
 * whose label is reached back through `pgellabelid`. `format_type(pgptypid,
 * pgptypmod)` renders the property's SQL type (e.g. `character varying(45)`).
 *
 * **Version guard:** pre-19 servers lack these catalogs entirely, so the query
 * would raise `relation "pg_catalog.pg_propgraph_label" does not exist`. We
 * skip all three round-trips when the major version is < 19 (or unknown) —
 * PG16 pays zero extra queries and the field degrades to empty. A defensive
 * `.success` check on the graph-list query keeps an unexpected failure silent.
 */
export async function fetchPgPropertyGraphs(
  connectionId: string,
  database: string,
  serverVersion: string,
): Promise<PropertyGraphMeta[]> {
  const major = parsePgMajorVersion(serverVersion);
  if (major === undefined || major < 19) return []; // pre-19: catalogs absent → skip

  const graphsSql = `
    SELECT c.oid AS graph_oid, c.relname AS graph_name, n.nspname AS graph_schema
    FROM pg_catalog.pg_class c
    JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
    WHERE c.relkind = 'g'
      AND n.nspname NOT IN ('pg_catalog', 'information_schema', 'pg_toast')
    ORDER BY n.nspname, c.relname;
  `;
  const labelsSql = `
    SELECT l.pglpgid AS graph_oid, l.pgllabel AS label_name, e.pgekind::text AS element_kind
    FROM pg_catalog.pg_propgraph_label l
    JOIN pg_catalog.pg_propgraph_element_label el ON el.pgellabelid = l.oid
    JOIN pg_catalog.pg_propgraph_element e ON e.oid = el.pgelelid
    ORDER BY l.pglpgid, l.pgllabel;
  `;
  const propsSql = `
    SELECT l.pglpgid AS graph_oid, l.pgllabel AS label_name,
           p.pgpname AS property_name,
           format_type(p.pgptypid, p.pgptypmod) AS data_type
    FROM pg_catalog.pg_propgraph_label_property lp
    JOIN pg_catalog.pg_propgraph_element_label el ON el.oid = lp.plpellabelid
    JOIN pg_catalog.pg_propgraph_label l ON l.oid = el.pgellabelid
    JOIN pg_catalog.pg_propgraph_property p ON p.oid = lp.plppropid
    ORDER BY l.pglpgid, l.pgllabel, p.oid;
  `;

  const graphRows = await probeRows(connectionId, database, 'graphs', graphsSql);
  if (graphRows.length === 0) return []; // no graphs (or the catalog probe failed)
  const [labelRows, propRows] = await Promise.all([
    probeRows(connectionId, database, 'graph-labels', labelsSql),
    probeRows(connectionId, database, 'graph-props', propsSql),
  ]);

  return assemblePgPropertyGraphs(graphRows, labelRows, propRows);
}

/**
 * Probe `version()` once per snapshot refresh so IntelliSense version gating
 * (INT-1) can compare the connected server against feature `addedIn`
 * versions. Returns `''` on failure — the bus projector maps empty to
 * `undefined`, which `classifyFeature` treats as "unknown / don't gate".
 */
export async function fetchPgServerVersion(
  connectionId: string,
  database: string,
): Promise<string> {
  const rows = await probeRows(connectionId, database, 'version', 'SELECT version() AS version;');
  const row = rows[0];
  return row ? String(row.version ?? '') : '';
}

/**
 * The session's search_path (`parseSearchPath`), or `null` when it could not
 * be read — the probe failed or returned an unreadable value. An empty list
 * is a real answer: the path is empty.
 */
export async function fetchPgSearchPath(connectionId: string, database: string): Promise<string[] | null> {
  // SHOW search_path returns one row with one column; current_user from a
  // separate select is faster than parsing pg_catalog directly and works
  // across the PG versions we support.
  const sql = `SELECT current_setting('search_path') AS sp, current_user AS usr;`;
  const rows = await probeRows(connectionId, database, 'searchpath', sql);
  const row = rows[0];
  if (!row) return null;
  const sp = row.sp ?? row.search_path;
  if (sp === null || sp === undefined) return null;
  const usr = String(row.usr ?? row.current_user ?? '') || null;
  return parseSearchPath(String(sp), usr);
}

/**
 * One column row of every non-system relation, PK flag and formatted type
 * folded in. `information_schema.columns` omits matviews entirely (they are
 * not in the SQL standard) — those columns come from the relation catalog's
 * `pg_attribute` join in phase 1 instead.
 *
 * The PK flag reads `pg_catalog` directly. It used to join
 * `information_schema.table_constraints` × `key_column_usage`, which
 * PostgreSQL evaluates with per-row privilege checks and conkey expansion —
 * and OFFSET paging re-evaluates the whole join for every page: 23.6 s per
 * 5,000-row page on a 1,500-table catalog (~94 s before the Visual Query
 * Editor could edit), 174 ms with a `pg_constraint` join. The rows still come
 * from the privilege-filtered `information_schema.columns`, so nothing new
 * becomes visible — but a PK now shows for a SELECT-only login too, which
 * `table_constraints` (owner / non-SELECT privilege only) used to hide.
 *
 * `formatted_type` is `format_type(atttypid, atttypmod)` — the spelling Table
 * Explorer's column query reads (`integer[]`, `character varying(10)`), which
 * `udt_name` (`_int4`, `varchar`) loses. Both it and the PK flag come from ONE
 * `pg_attribute` scan of the user relations (`m`): the per-attribute PK test
 * is a `pg_constraint` probe. On the 1,500-table × 12-column catalog a
 * 5,000-row page measured 35–40 ms both before and after this join replaced
 * the PK-only one (a second, separate attribute join cost ~65 ms), and the
 * rows (formatted type aside) are byte-identical to the PK-only query's on
 * dvdrental and on that catalog.
 */
export function buildPgColumnsPageSql(pageClause: string): string {
  return `SELECT c.table_schema, c.table_name, c.column_name, c.data_type, c.udt_name,
      c.is_identity, c.column_default,
      COALESCE(m.is_primary_key, false) AS is_primary_key,
      m.formatted_type
    FROM information_schema.columns c
    LEFT JOIN (
      SELECT ns.nspname::information_schema.sql_identifier AS table_schema,
             cl.relname::information_schema.sql_identifier AS table_name,
             a.attname::information_schema.sql_identifier AS column_name,
             pg_catalog.format_type(a.atttypid, a.atttypmod) AS formatted_type,
             pk.conrelid IS NOT NULL AS is_primary_key
      FROM pg_catalog.pg_attribute a
      JOIN pg_catalog.pg_class cl ON cl.oid = a.attrelid
      JOIN pg_catalog.pg_namespace ns ON ns.oid = cl.relnamespace
      LEFT JOIN pg_catalog.pg_constraint pk
        ON pk.conrelid = a.attrelid AND pk.contype = 'p' AND a.attnum = ANY (pk.conkey)
      WHERE a.attnum > 0
        AND NOT a.attisdropped
        AND cl.relkind IN ('r', 'p', 'v', 'f')
        AND ns.nspname NOT IN ('pg_catalog', 'information_schema')
    ) m
      ON c.table_schema = m.table_schema
     AND c.table_name = m.table_name
     AND c.column_name = m.column_name
    WHERE c.table_schema NOT IN ('pg_catalog', 'information_schema')
    ORDER BY c.table_schema, c.table_name, c.ordinal_position
    ${pageClause}`;
}

/** A column plus the relation it belongs to — the phase-2 column page item. */
export type PgColumnIntrospection = {
  readonly schema: string;
  readonly table: string;
  readonly column: PgSchemaColumn;
};

export function aggregatePgColumnRows(
  rows: readonly IntrospectionRow[],
  enumTypes: ReadonlyMap<string, string[]>,
): PgColumnIntrospection[] {
  const out: PgColumnIntrospection[] = [];
  for (const row of rows) {
    const schema = String(row.table_schema ?? 'public');
    // Catalog spelling; consumers key it folded (`pgTableKey`).
    const table = String(row.table_name ?? '');
    if (!table) continue;
    out.push({ schema, table, column: mapPgColumnRow(row, enumTypes) });
  }
  return out;
}

/**
 * Page every column of every non-system relation into `sink` (phase 2).
 * `enumTypes` must already be fetched — a column's `enumValues` come from the
 * `pg_enum` probe, and re-reading it per page would multiply the round-trips.
 */
export async function fetchPgColumnPages(
  connectionId: string,
  database: string,
  enumTypes: ReadonlyMap<string, string[]>,
  sink: PgPageSink<PgColumnIntrospection>,
  options?: PagedFetchOptions,
): Promise<void> {
  await pagePgArtefact<PgColumnIntrospection>({
    connectionId,
    database,
    label: 'cols',
    artefact: 'columns',
    totalBudget: COLUMN_ROW_BUDGET,
    sqlForPage: buildPgColumnsPageSql,
    aggregate: (rows) => aggregatePgColumnRows(rows, enumTypes),
    sink,
    shouldAbort: options?.shouldAbort,
  });
}

/**
 * Build a COMPLETE snapshot in one call — phase 1 and phase 2 accumulated
 * without publishing. `pgSchemaCache` no longer uses this (it drives the two
 * phases itself so the relation list can publish first); it stays as the
 * one-shot form for callers that just want the finished object, and for the
 * unit tests that exercise the query set end to end.
 */
export async function buildPgSchemaSnapshot(connectionId: string, database: string): Promise<PgSchemaSnapshot> {
  // Probe the server version up front so `fetchPgPropertyGraphs` can version-gate
  // its PG19-only catalog queries (a pre-19 server would error on the
  // `pg_propgraph_*` tables). The extra round-trip is a metadata-only
  // `SELECT version()` — negligible next to the column scan below.
  const serverVersion = await fetchPgServerVersion(connectionId, database).catch(() => '');

  const [
    probedSearchPath,
    foreignKeys,
    extensions,
    enumTypes,
    propertyGraphs,
    relationCatalog,
    userTypes,
    routinesByNameLower,
  ] = await Promise.all([
    fetchPgSearchPath(connectionId, database).catch(() => null),
    fetchPgForeignKeys(connectionId, database).catch(() => [] as FkEdge[]),
    fetchPgExtensions(connectionId, database).catch(() => [] as string[]),
    fetchPgEnumTypes(connectionId, database).catch(() => new Map<string, string[]>()),
    fetchPgPropertyGraphs(connectionId, database, serverVersion).catch(
      () => [] as PropertyGraphMeta[],
    ),
    fetchPgRelationCatalog(connectionId, database).catch(
      (): PgRelationCatalog => ({ kinds: new Map(), matviews: [], sequences: [] }),
    ),
    fetchPgUserTypes(connectionId, database).catch(() => [] as PgUserType[]),
    fetchRoutines(connectionId, database).catch(
      () => ({}) as Record<string, RoutineMetadata[]>,
    ),
  ]);

  const sequences = relationCatalog.sequences;
  const columnsByTableLower: Record<string, PgSchemaColumn[]> = {};
  const tablesBySchemaTable = new Map<string, PgSchemaTableEntry>();

  await fetchPgColumnPages(connectionId, database, enumTypes, (page) => {
    for (const item of page) {
      (columnsByTableLower[item.table.toLowerCase()] ??= []).push(item.column);
      const key = pgRelationKey(item.schema, item.table);
      let entry = tablesBySchemaTable.get(key);
      if (!entry) {
        entry = {
          schema: item.schema,
          name: item.table,
          columns: [],
          // `information_schema.columns` carries no relkind, so the kind comes
          // from the `pg_class` catalog scan. Unknown (catalog probe failed) →
          // 'table', which is what every entry used to be unconditionally.
          kind: relationCatalog.kinds.get(key) ?? 'table',
        };
        tablesBySchemaTable.set(key, entry);
      }
      entry.columns.push(item.column);
    }
  });

  // Matviews are invisible to `information_schema.columns`, so they never
  // appeared in the loop above — append them (with their `pg_attribute`
  // columns) unless the column scan somehow already produced the same key.
  for (const mv of relationCatalog.matviews) {
    const key = pgRelationKey(mv.schema, mv.name);
    if (tablesBySchemaTable.has(key)) continue;
    tablesBySchemaTable.set(key, mv);
    const bucket = mv.name.toLowerCase();
    if (!columnsByTableLower[bucket]) columnsByTableLower[bucket] = mv.columns;
  }

  return {
    connectionId,
    database,
    builtAt: Date.now(),
    columnsByTableLower,
    tables: Array.from(tablesBySchemaTable.values()),
    routinesByNameLower,
    searchPath: probedSearchPath ?? [],
    searchPathKnown: probedSearchPath !== null,
    foreignKeys,
    extensions,
    serverVersion,
    userTypes,
    propertyGraphs,
    sequences,
    phase: 'complete',
  };
}

/**
 * Map one `information_schema.columns` row onto a `PgSchemaColumn`. Exported
 * pure so the identity / default classification is unit-testable without the
 * IPC bridge.
 *
 * `is_identity = 'YES'` is PG 10+ `GENERATED … AS IDENTITY`. A `SERIAL`
 * column reports `is_identity = 'NO'` with a `nextval(…)` default — the same
 * engine-assigns-the-value mechanism in pre-10 spelling — so both fold into
 * `isIdentity`. Any other default only sets `hasDefault` (`logged_at
 * TIMESTAMP DEFAULT CURRENT_TIMESTAMP` is a perfectly ordinary INSERT
 * target).
 */
export function mapPgColumnRow(
  row: Record<string, unknown>,
  enumTypes: ReadonlyMap<string, string[]>,
): PgSchemaColumn {
  const dataType = String(row.udt_name ?? row.data_type ?? '');
  const formattedType = row.formatted_type == null ? '' : String(row.formatted_type);
  const enumMembers = enumTypes.get(dataType);
  const columnDefault = row.column_default == null ? '' : String(row.column_default).trim();
  const declaredIdentity = String(row.is_identity ?? '').toUpperCase() === 'YES';
  const serialDefault = /^nextval\s*\(/i.test(columnDefault);
  return {
    name: String(row.column_name ?? ''),
    dataType,
    ...(formattedType ? { formattedType } : {}),
    isPrimaryKey: row.is_primary_key === true || String(row.is_primary_key) === 'true',
    ...(enumMembers && enumMembers.length > 0 ? { enumValues: enumMembers } : {}),
    ...(declaredIdentity || serialDefault ? { isIdentity: true } : {}),
    ...(columnDefault ? { hasDefault: true } : {}),
  };
}
